/**
 * Catálogo de construcción (paso 1.3): los elementos del v6 en sus 7 categorías, con nombre de
 * negocio, medidas reales (del v6), rol operativo y la etiqueta «Simula» para lo que afecta al
 * modelo. Lo que aún no simula dice «Solo visual» y desde qué paso simula.
 *
 * Cada elemento declara como datos su boca de acceso y su símbolo de planta, para que la 1.4 solo los
 * dibuje (doc. 06 §2). Los 12 pisos con acabado, el muro y la zona de piso libre (avance 4 de la
 * 1.4) son formas con puntos: polígonos con material y una línea con espesor. Con ellos el catálogo
 * tiene los 81 elementos del v6. Los modelos 3D son propios, generados por código.
 *
 * Dos conceptos:
 * - **elemento** (`ItemDeCatalogo`): lo que se guarda en el layout (`catalogoId`);
 * - **entrada** (`EntradaDeCatalogo`): cada botón del catálogo. Los tres elevadores son tres entradas
 *   del mismo elemento con otro tipo de bahía; las vías, los pisos y el muro son entradas que crean
 *   formas (`Via`, `Piso`, `Muro`), no elementos.
 */

import type {
  AcabadoPiso,
  BocaDeAcceso,
  Catalogo,
  CategoriaCatalogo,
  Elemento,
  ItemDeCatalogo,
  Layout,
  Muro,
  Piso,
  Punto,
  SimboloDePlanta,
  TipoBahia,
  TipoVia,
} from '../nucleo/dominio/espacio';
import { colocacionSugerida } from '../nucleo/espacial/bocas';
import { rectangulo } from '../nucleo/espacial/formas';
import { normalizarAngulo } from '../nucleo/espacial/geometria';

/**
 * Tipos de elevador. Qué servicios atiende cada uno lo dice cada servicio (paso 1.2, «Servicios»);
 * aquí solo queda si se prefiere para los servicios que lo admiten y si se simula.
 */
const BAHIAS: Record<TipoBahia, { nombre: string; especializada: boolean; activa: boolean }> = {
  mixto: { nombre: 'Mixto', especializada: false, activa: true },
  'fast-lane': { nombre: 'Fast Lane', especializada: true, activa: true },
  mayor: { nombre: 'Mayor', especializada: true, activa: true },
  inactivo: { nombre: 'Inactivo (no se simula)', especializada: false, activa: false },
};

export const CATEGORIAS: readonly { id: CategoriaCatalogo; nombre: string; icono: string }[] = [
  { id: 'pisos', nombre: 'Pisos y vías', icono: 'via' },
  { id: 'operacion', nombre: 'Operación', icono: 'elevador' },
  { id: 'puntos-clave', nombre: 'Puntos clave', icono: 'puntoClave' },
  { id: 'equipos', nombre: 'Equipos de taller', icono: 'llave' },
  { id: 'clientes', nombre: 'Clientes y showroom', icono: 'sillon' },
  { id: 'exterior', nombre: 'Exterior', icono: 'arbol' },
  { id: 'estructura', nombre: 'Estructura', icono: 'columna' },
];

// ─────────────────────────── ayudas para declarar ───────────────────────────

const VEHICULAR = (forma: BocaDeAcceso['forma']): BocaDeAcceso => ({
  forma,
  circulacion: 'vehicular',
});
const PEATONAL = (forma: BocaDeAcceso['forma']): BocaDeAcceso => ({
  forma,
  circulacion: 'peatonal',
});
const rect = (familia: SimboloDePlanta['familia'], rotulo?: string): SimboloDePlanta => ({
  forma: 'rectangulo',
  familia,
  ...(rotulo ? { rotulo } : {}),
});
const circulo = (familia: SimboloDePlanta['familia']): SimboloDePlanta => ({
  forma: 'circulo',
  familia,
});
const MARCA: SimboloDePlanta = { forma: 'marca', familia: 'pintura' };

type Medidas = [ancho: number, fondo: number, alto: number];

/** Elemento «Solo visual»: sólido, sin boca, con el alto editable. */
function visual(
  id: string,
  nombre: string,
  categoria: CategoriaCatalogo,
  [ancho, fondo, alto]: Medidas,
  descripcion: string,
  simbolo: SimboloDePlanta,
  extra: Partial<ItemDeCatalogo> = {},
): ItemDeCatalogo {
  return {
    id,
    nombre,
    categoria,
    descripcion,
    dimensionesPorDefecto: { ancho, fondo, alto },
    simula: false,
    boca: null,
    simbolo,
    solido: true,
    editable: { alto: true },
    licencia: 'propia',
    ...extra,
  };
}

/** Marca pintada en el piso: no ocupa lugar ni se superpone. */
function marca(
  id: string,
  nombre: string,
  [ancho, fondo, alto]: Medidas,
  descripcion: string,
  extra: Partial<ItemDeCatalogo> = {},
): ItemDeCatalogo {
  return {
    id,
    nombre,
    categoria: 'pisos',
    descripcion,
    dimensionesPorDefecto: { ancho, fondo, alto },
    simula: false,
    boca: null,
    simbolo: MARCA,
    solido: false,
    licencia: 'propia',
    ...extra,
  };
}

const EQUIPO = 'Equipo de taller. Solo visual: no cambia los resultados.';
const MUEBLE = 'Mobiliario para clientes. Solo visual: no cambia los resultados.';
const EXTERIOR = 'Elemento del exterior. Solo visual: no cambia los resultados.';

// ─────────────────────────────── elementos ───────────────────────────────

const items: ItemDeCatalogo[] = [
  // ── Pisos y vías: marcas de piso (las vías son entradas que dibujan) ──
  marca('flecha-piso', 'Flecha de piso', [1.2, 3, 0.01], 'Flecha pintada que indica el sentido.'),
  marca(
    'linea-pare',
    'Línea de pare',
    [3.5, 2, 0.01],
    'Línea y letrero «PARE» pintados en el piso.',
  ),
  marca(
    'rompemuelle',
    'Rompemuelle',
    [3.5, 0.6, 0.1],
    'Reductor de velocidad sobre la vía. Solo visual: no cambia la velocidad de los autos.',
  ),
  marca(
    'estacionamiento-clientes',
    'Estacionamiento de clientes',
    [2.5, 5, 0.01],
    'Cajón pintado para autos de clientes. Solo visual: los clientes no se simulan hasta la 1.6.',
    { boca: VEHICULAR('detras'), editable: { texto: 'Texto pintado' } },
  ),

  // ── Operación ──
  {
    id: 'elevador',
    nombre: 'Elevador',
    categoria: 'operacion',
    descripcion: 'Bahía con elevador de dos columnas. Su parte trasera debe mirar a una vía.',
    dimensionesPorDefecto: { ancho: 3.7, fondo: 6.4, alto: 2.2 },
    medidasMinimas: { ancho: 3.0, fondo: 5.2 },
    simula: true,
    rolOperativo: { estacion: 'bahia', capacidad: 1, sigueTurno: true, variantes: BAHIAS },
    boca: VEHICULAR('detras'),
    simbolo: rect('bahia'),
    solido: true,
    licencia: 'propia',
  },
  {
    id: 'cajon-recepcion',
    nombre: 'Cajón de recepción',
    categoria: 'operacion',
    descripcion: 'Donde el asesor recibe el auto. Entra por detrás y sale por delante si hay vía.',
    dimensionesPorDefecto: { ancho: 2.6, fondo: 5.0, alto: 0.05 },
    medidasMinimas: { ancho: 2.3, fondo: 4.6 },
    simula: true,
    rolOperativo: { estacion: 'recepcion', capacidad: 1, sigueTurno: true },
    boca: VEHICULAR('paso'),
    simbolo: rect('cajon', 'R'),
    solido: true,
    licencia: 'propia',
  },
  {
    id: 'cajon-patio',
    nombre: 'Cajón de patio',
    categoria: 'operacion',
    descripcion:
      'Espacio de espera: el auto espera aquí en lugar de bloquear una recepción, un elevador o el lavado.',
    dimensionesPorDefecto: { ancho: 2.5, fondo: 5.0, alto: 0.05 },
    medidasMinimas: { ancho: 2.3, fondo: 4.6 },
    simula: true,
    rolOperativo: { estacion: 'patio', capacidad: 1, sigueTurno: false },
    boca: VEHICULAR('detras'),
    simbolo: rect('cajon', 'P'),
    solido: true,
    licencia: 'propia',
  },
  {
    id: 'lavado',
    nombre: 'Puesto de lavado',
    categoria: 'operacion',
    descripcion: 'Lavado con pórtico. Un auto a la vez; sigue el turno del taller.',
    dimensionesPorDefecto: { ancho: 3.0, fondo: 5.8, alto: 2.6 },
    medidasMinimas: { ancho: 2.6, fondo: 5.0 },
    simula: true,
    rolOperativo: { estacion: 'lavado', capacidad: 1, sigueTurno: true },
    boca: VEHICULAR('detras'),
    simbolo: rect('lavado', 'L'),
    solido: true,
    licencia: 'propia',
  },
  {
    id: 'cajon-entrega',
    nombre: 'Cajón de entrega',
    categoria: 'operacion',
    descripcion: 'Donde el auto listo espera a que el cliente lo retire.',
    dimensionesPorDefecto: { ancho: 2.5, fondo: 5.0, alto: 0.05 },
    medidasMinimas: { ancho: 2.3, fondo: 4.6 },
    simula: true,
    rolOperativo: { estacion: 'entrega', capacidad: 1, sigueTurno: false },
    boca: VEHICULAR('detras'),
    simbolo: rect('cajon', 'E'),
    solido: true,
    licencia: 'propia',
  },

  // ── Puntos clave ──
  {
    id: 'oficina-asesores',
    nombre: 'Oficina de asesores',
    categoria: 'puntos-clave',
    descripcion:
      'Referencia visual. Los recorridos a pie se simulan desde la 1.6: hoy no cambia los resultados.',
    dimensionesPorDefecto: { ancho: 10, fondo: 7, alto: 2.6 },
    simula: false,
    simulaDesde: '1.6',
    unico: true,
    boca: PEATONAL('delante'),
    simbolo: rect('edificio', 'OF'),
    solido: true,
    contenedor: true,
    licencia: 'propia',
  },
  {
    id: 'torre-control',
    nombre: 'Torre de control',
    categoria: 'puntos-clave',
    descripcion:
      'Donde se asignan las OT. Se simula desde la 1.6, con el controlador: hoy no cambia los resultados.',
    dimensionesPorDefecto: { ancho: 2.4, fondo: 2.4, alto: 8 },
    simula: false,
    simulaDesde: '1.6',
    unico: true,
    boca: PEATONAL('delante'),
    simbolo: circulo('edificio'),
    solido: true,
    editable: { alto: true },
    licencia: 'propia',
  },
  {
    id: 'almacen-repuestos',
    nombre: 'Almacén de repuestos',
    categoria: 'puntos-clave',
    descripcion:
      'Estanterías y mostrador de repuestos. Se simula desde la 1.6: hoy no cambia los resultados.',
    dimensionesPorDefecto: { ancho: 8, fondo: 5, alto: 3 },
    simula: false,
    simulaDesde: '1.6',
    unico: true,
    boca: PEATONAL('delante'),
    simbolo: rect('edificio', 'ALM'),
    solido: true,
    contenedor: true,
    editable: { alto: true, texto: 'Letrero' },
    licencia: 'propia',
  },
  {
    id: 'ingreso',
    nombre: 'Ingreso vehicular',
    categoria: 'puntos-clave',
    descripcion: 'Punto por donde llegan los autos. Debe estar sobre una vía (a menos de 6 m).',
    dimensionesPorDefecto: { ancho: 2.5, fondo: 2.5, alto: 0.1 },
    simula: true,
    unico: true,
    rolOperativo: { estacion: 'ingreso', capacidad: null, sigueTurno: false },
    boca: VEHICULAR('marcador'),
    simbolo: { forma: 'circulo', familia: 'acceso', rotulo: 'ING' },
    solido: false,
    licencia: 'propia',
  },
  {
    id: 'salida',
    nombre: 'Salida vehicular',
    categoria: 'puntos-clave',
    descripcion: 'Punto por donde salen los autos. Debe estar sobre una vía (a menos de 6 m).',
    dimensionesPorDefecto: { ancho: 2.5, fondo: 2.5, alto: 0.1 },
    simula: true,
    unico: true,
    rolOperativo: { estacion: 'salida', capacidad: null, sigueTurno: false },
    boca: VEHICULAR('marcador'),
    simbolo: { forma: 'circulo', familia: 'acceso', rotulo: 'SAL' },
    solido: false,
    licencia: 'propia',
  },
  {
    id: 'acceso-peatonal',
    nombre: 'Acceso peatonal',
    categoria: 'puntos-clave',
    descripcion:
      'Por donde entran los clientes a pie. Se simula desde la 1.6: hoy no cambia los resultados.',
    dimensionesPorDefecto: { ancho: 1.6, fondo: 1.6, alto: 0.1 },
    simula: false,
    simulaDesde: '1.6',
    unico: true,
    boca: PEATONAL('marcador'),
    simbolo: { forma: 'circulo', familia: 'acceso', rotulo: 'PEA' },
    solido: false,
    licencia: 'propia',
  },

  // ── Equipos de taller (21) ──
  visual('rack', 'Rack / estantería', 'equipos', [1, 4, 1.8], EQUIPO, rect('equipo', 'RACK')),
  visual('mesa-trabajo', 'Mesa de trabajo', 'equipos', [1.8, 0.9, 0.9], EQUIPO, rect('equipo')),
  visual(
    'carro-herramientas',
    'Carro de herramientas',
    'equipos',
    [0.9, 0.6, 1],
    EQUIPO,
    rect('equipo'),
  ),
  visual(
    'alineadora',
    'Alineadora',
    'equipos',
    [4.8, 2.4, 0.25],
    'Plataforma de alineamiento. Solo visual: hoy no hay un servicio que la use.',
    rect('equipo', 'ALI'),
  ),
  visual('balanceadora', 'Balanceadora', 'equipos', [1.2, 0.9, 1.2], EQUIPO, rect('equipo', 'BAL')),
  visual(
    'desmontadora',
    'Desmontadora de llantas',
    'equipos',
    [1, 1.1, 1],
    EQUIPO,
    rect('equipo', 'DES'),
  ),
  visual(
    'escaner',
    'Escáner de diagnóstico',
    'equipos',
    [0.6, 0.5, 1.1],
    EQUIPO,
    rect('equipo', 'ESC'),
  ),
  visual(
    'cargador-baterias',
    'Cargador de baterías',
    'equipos',
    [0.5, 0.4, 0.8],
    EQUIPO,
    rect('equipo'),
  ),
  visual('compresor', 'Compresor', 'equipos', [1.6, 0.7, 1.2], EQUIPO, rect('equipo', 'COM')),
  visual('grua-motor', 'Grúa de motor', 'equipos', [1, 1.8, 2], EQUIPO, rect('equipo', 'GRÚA')),
  visual('gata-hidraulica', 'Gata hidráulica', 'equipos', [0.4, 1, 0.3], EQUIPO, rect('equipo')),
  visual(
    'recuperador-aceite',
    'Recuperador de aceite',
    'equipos',
    [0.7, 0.7, 1.6],
    EQUIPO,
    circulo('equipo'),
  ),
  visual('lavadero-piezas', 'Lavadero de piezas', 'equipos', [1.2, 0.7, 1], EQUIPO, rect('equipo')),
  visual(
    'rack-neumaticos',
    'Rack de neumáticos',
    'equipos',
    [2.2, 0.7, 1.4],
    EQUIPO,
    rect('equipo', 'NEU'),
  ),
  visual(
    'tambores-aceite',
    'Tambores de aceite usado',
    'equipos',
    [1.2, 1.2, 1],
    EQUIPO,
    rect('equipo'),
  ),
  visual(
    'puntos-residuos',
    'Puntos de residuos',
    'equipos',
    [2.4, 0.6, 1],
    EQUIPO,
    rect('equipo', 'RES'),
  ),
  visual('lockers', 'Lockers del personal', 'equipos', [2.7, 0.5, 1.9], EQUIPO, rect('equipo')),
  visual(
    'tablero-gestion',
    'Tablero de gestión visual',
    'equipos',
    [2.2, 0.6, 2],
    EQUIPO,
    rect('equipo', 'TAB'),
  ),
  visual('extintor', 'Extintor', 'equipos', [0.4, 0.4, 0.9], EQUIPO, circulo('equipo')),
  visual('botiquin', 'Botiquín', 'equipos', [0.5, 0.2, 0.5], EQUIPO, rect('equipo')),
  visual('senal-seguridad', 'Señal de seguridad', 'equipos', [0.6, 0.3, 2], EQUIPO, rect('equipo')),

  // ── Clientes y showroom (9) ──
  visual('sala-espera', 'Sala de espera', 'clientes', [4, 3.5, 1], MUEBLE, rect('mobiliario'), {
    contenedor: true,
  }),
  visual('sillon', 'Sillón', 'clientes', [1, 0.9, 0.9], MUEBLE, rect('mobiliario')),
  visual(
    'mostrador',
    'Mostrador de atención',
    'clientes',
    [3, 0.8, 1.05],
    MUEBLE,
    rect('mobiliario'),
    {
      editable: { alto: true, texto: 'Letrero' },
    },
  ),
  visual(
    'estacion-cafe',
    'Estación de café',
    'clientes',
    [1.2, 0.6, 1.2],
    MUEBLE,
    rect('mobiliario'),
  ),
  visual(
    'pantalla-turnos',
    'Pantalla de turnos',
    'clientes',
    [1.3, 0.5, 1.4],
    MUEBLE,
    rect('mobiliario'),
  ),
  visual(
    'kiosco',
    'Kiosco de autoservicio',
    'clientes',
    [0.7, 0.6, 1.5],
    MUEBLE,
    rect('mobiliario'),
  ),
  visual(
    'auto-exhibicion',
    'Auto en exhibición',
    'clientes',
    [6, 6, 1.7],
    'Auto genérico sobre una tarima. Solo visual: no es una unidad de la simulación.',
    circulo('mobiliario'),
  ),
  visual(
    'totem',
    'Tótem de marca',
    'clientes',
    [1, 0.4, 3.2],
    'Tótem genérico, sin marcas de terceros. Solo visual.',
    rect('mobiliario'),
  ),
  visual(
    'planta-decorativa',
    'Planta decorativa',
    'clientes',
    [0.8, 0.8, 1.4],
    MUEBLE,
    circulo('vegetacion'),
  ),

  // ── Exterior (11) ──
  visual('arbol', 'Árbol', 'exterior', [3, 3, 5], EXTERIOR, circulo('vegetacion')),
  visual('jardinera', 'Jardinera', 'exterior', [2, 0.8, 1], EXTERIOR, rect('vegetacion')),
  visual('poste-luz', 'Poste de luz', 'exterior', [0.5, 0.5, 6], EXTERIOR, circulo('urbano')),
  visual('cono', 'Cono de tránsito', 'exterior', [0.45, 0.45, 0.7], EXTERIOR, circulo('urbano')),
  visual('bolardo', 'Bolardo', 'exterior', [0.25, 0.25, 0.9], EXTERIOR, circulo('urbano')),
  visual(
    'caseta',
    'Caseta de vigilancia',
    'exterior',
    [2, 2, 2.7],
    EXTERIOR,
    rect('edificio', 'VIG'),
  ),
  visual('reja', 'Cerco / reja', 'exterior', [6, 0.1, 2], EXTERIOR, rect('urbano')),
  visual('banca', 'Banca', 'exterior', [1.6, 0.5, 0.45], EXTERIOR, rect('urbano')),
  visual(
    'auto-estacionado',
    'Auto estacionado',
    'exterior',
    [1.9, 4.6, 1.6],
    'Auto genérico estacionado. Solo visual: no es una unidad de la simulación.',
    rect('urbano'),
  ),
  visual(
    'barrera',
    'Barrera vehicular',
    'exterior',
    [4, 0.4, 1.1],
    'Barrera de control de acceso. Solo visual: no detiene a los autos de la simulación.',
    rect('urbano'),
    { solido: false, boca: VEHICULAR('paso') },
  ),
  visual(
    'porton',
    'Portón',
    'exterior',
    [6, 0.3, 2.6],
    'Portón de acceso vehicular. Solo visual: no cierra el paso en la simulación.',
    rect('urbano'),
    { solido: false, boca: VEHICULAR('paso') },
  ),

  // ── Estructura (6 en la 1.3; el muro y la zona de piso libre llegan con la 1.4) ──
  visual(
    'columna',
    'Columna',
    'estructura',
    [0.5, 0.5, 4],
    'Columna de la nave. Solo visual: no bloquea vías en la simulación.',
    rect('obra'),
  ),
  visual(
    'edificio-contexto',
    'Edificio de contexto',
    'estructura',
    [10, 8, 3.5],
    'Volumen de un edificio vecino o de otra área. Solo visual.',
    rect('obra', 'EDIF'),
    { contenedor: true, editable: { alto: true, texto: 'Letrero' } },
  ),
  visual(
    'techado',
    'Techado',
    'estructura',
    [8, 8, 4.2],
    'Techo liviano sobre columnas. Solo visual: no ocupa el piso.',
    rect('obra'),
    { solido: false },
  ),
  {
    ...marca('rotulo', 'Rótulo', [4, 1, 0.01], 'Texto pintado en el piso para nombrar una zona.', {
      editable: { texto: 'Texto' },
    }),
    categoria: 'estructura',
  },
  visual(
    'puerta',
    'Puerta',
    'estructura',
    [1, 0.6, 2.1],
    'Puerta peatonal. Solo visual.',
    rect('obra'),
    { solido: false, boca: PEATONAL('paso') },
  ),
  visual(
    'ventanal',
    'Ventanal',
    'estructura',
    [4, 0.15, 2.6],
    'Paño de vidrio. Solo visual.',
    rect('obra'),
  ),
];

export const CATALOGO: Catalogo = Object.fromEntries(items.map((i) => [i.id, i]));

export const ITEMS_CATALOGO: readonly ItemDeCatalogo[] = items;

export const TIPOS_BAHIA = BAHIAS;

/** Texto inicial de los elementos con texto (rótulo, letreros). */
export const TEXTO_INICIAL: Readonly<Record<string, string>> = {
  rotulo: 'RÓTULO',
  'estacionamiento-clientes': 'CLIENTES',
  'edificio-contexto': 'EDIFICIO',
  'almacen-repuestos': 'ALMACÉN',
  mostrador: 'ATENCIÓN',
};

// ─────────────────────────────── entradas ───────────────────────────────

export interface EntradaElemento {
  tipo: 'elemento';
  id: string;
  nombre: string;
  categoria: CategoriaCatalogo;
  itemId: string;
  propiedades?: Elemento['propiedades'];
}

export interface EntradaVia {
  tipo: 'via';
  id: string;
  nombre: string;
  categoria: 'pisos';
  tipoVia: TipoVia;
  descripcion: string;
  simula: boolean;
  simulaDesde?: string;
}

/**
 * Piso con acabado (paso 1.4): un rectángulo que se coloca con un clic y luego se edita como
 * polígono, o una zona libre (`medidas = null`) que se dibuja punto a punto.
 */
export interface EntradaPiso {
  tipo: 'piso';
  id: string;
  nombre: string;
  categoria: 'pisos' | 'estructura';
  acabado: AcabadoPiso;
  medidas: { ancho: number; fondo: number } | null;
  descripcion: string;
}

/** Muro (paso 1.4): se dibuja punto a punto, como una vía. */
export interface EntradaMuro {
  tipo: 'muro';
  id: string;
  nombre: string;
  categoria: 'estructura';
  descripcion: string;
}

export type EntradaDeCatalogo = EntradaElemento | EntradaVia | EntradaPiso | EntradaMuro;

/** Nombre de cada acabado de piso (Propiedades y miniaturas). */
export const ACABADOS: Readonly<Record<AcabadoPiso, string>> = {
  epoxico: 'Epóxico de taller',
  concreto: 'Concreto',
  asfalto: 'Asfalto',
  calzada: 'Calzada vehicular',
  vereda: 'Vereda',
  cesped: 'Área verde',
  ceramico: 'Cerámico',
  recepcion: 'Recepción',
  lavado: 'Lavado',
  entrega: 'Entrega',
  seguridad: 'Franja de seguridad',
  cebra: 'Paso peatonal (cebra)',
};

/** Muro por defecto (como el v6): 25 cm de espesor y 2.2 m de alto. */
export const MURO_POR_DEFECTO = { espesor: 0.25, alto: 2.2 } as const;

const SOLO_PISO = 'Solo visual: es un acabado del piso; no cambia la simulación.';

/** Los 12 pisos del v6, en su orden, con sus medidas iniciales. */
const PISOS: EntradaPiso[] = (
  [
    ['piso-epoxico', 'Piso epóxico de taller', 'epoxico', 8, 6],
    ['piso-concreto', 'Piso de concreto', 'concreto', 8, 6],
    ['piso-asfalto', 'Asfalto', 'asfalto', 8, 6],
    ['piso-calzada', 'Calzada vehicular', 'calzada', 6, 12],
    ['piso-vereda', 'Vereda', 'vereda', 2, 10],
    ['piso-cesped', 'Área verde', 'cesped', 6, 4],
    ['piso-ceramico', 'Piso cerámico', 'ceramico', 6, 5],
    ['piso-recepcion', 'Piso de recepción', 'recepcion', 8, 6],
    ['piso-lavado', 'Piso de lavado', 'lavado', 6, 6],
    ['piso-entrega', 'Piso de entrega', 'entrega', 8, 6],
    ['piso-seguridad', 'Franja de seguridad', 'seguridad', 6, 0.6],
    ['piso-cebra', 'Paso peatonal (cebra)', 'cebra', 3, 4],
  ] as const
).map(([id, nombre, acabado, ancho, fondo]) => ({
  tipo: 'piso',
  id,
  nombre,
  categoria: 'pisos',
  acabado,
  medidas: { ancho, fondo },
  descripcion:
    acabado === 'calzada'
      ? 'Solo visual: un piso de asfalto pintado como calzada. Los autos no lo usan; para que circulen, dibuja una vía.'
      : `${SOLO_PISO} Se coloca con un clic y se edita como polígono (manijas en sus esquinas).`,
}));

const MURO: EntradaMuro = {
  tipo: 'muro',
  id: 'muro',
  nombre: 'Muro',
  categoria: 'estructura',
  descripcion:
    'Solo visual: un muro de 25 cm de espesor y 2.2 m de alto. Haz clic en cada esquina; Enter o doble clic para terminar.',
};

const ZONA_LIBRE: EntradaPiso = {
  tipo: 'piso',
  id: 'zona-libre',
  nombre: 'Zona de piso (libre)',
  categoria: 'estructura',
  acabado: 'epoxico',
  medidas: null,
  descripcion: `${SOLO_PISO} Haz clic en cada esquina del polígono; Enter o doble clic para cerrarlo. El acabado se elige en Propiedades.`,
};

/** Ancho de calzada por tipo de vía (m). */
export const ANCHO_VIA: Record<TipoVia, number> = {
  'vehicular-doble': 6,
  'vehicular-un-sentido': 3.5,
  peatonal: 1.5,
};

const VIAS: EntradaVia[] = [
  {
    tipo: 'via',
    id: 'via-doble',
    nombre: 'Vía de doble sentido',
    categoria: 'pisos',
    tipoVia: 'vehicular-doble',
    descripcion: 'Calzada de 6 m con circulación en los dos sentidos.',
    simula: true,
  },
  {
    tipo: 'via',
    id: 'via-un-sentido',
    nombre: 'Vía de un sentido',
    categoria: 'pisos',
    tipoVia: 'vehicular-un-sentido',
    descripcion: 'Calzada de 3.5 m. Dibújala en el sentido de circulación.',
    simula: true,
  },
  {
    tipo: 'via',
    id: 'senda-peatonal',
    nombre: 'Senda peatonal',
    categoria: 'pisos',
    tipoVia: 'peatonal',
    descripcion:
      'Camino de 1.5 m para personas. Los autos no la usan; los recorridos a pie se simulan desde la 1.6.',
    simula: false,
    simulaDesde: '1.6',
  },
];

const BAHIA_DE_ENTRADA: [string, string, TipoBahia][] = [
  ['elevador-mixto', 'Elevador mixto', 'mixto'],
  ['elevador-fast-lane', 'Elevador Fast Lane', 'fast-lane'],
  ['elevador-mayor', 'Elevador mayor', 'mayor'],
];

function entradasDeElementos(): EntradaElemento[] {
  const r: EntradaElemento[] = [];
  for (const i of items) {
    if (i.id === 'elevador') {
      for (const [id, nombre, tipoBahia] of BAHIA_DE_ENTRADA)
        r.push({
          tipo: 'elemento',
          id,
          nombre,
          categoria: 'operacion',
          itemId: 'elevador',
          propiedades: { tipoBahia },
        });
      continue;
    }
    r.push({ tipo: 'elemento', id: i.id, nombre: i.nombre, categoria: i.categoria, itemId: i.id });
  }
  return r;
}

/** Botones del catálogo, en el orden del v6 dentro de cada categoría (81 en total). */
export const ENTRADAS: readonly EntradaDeCatalogo[] = (() => {
  const todas = [...PISOS, ...VIAS, MURO, ...entradasDeElementos(), ZONA_LIBRE];
  const orden = new Map(CATEGORIAS.map((c, i) => [c.id, i]));
  return todas
    .map((e, i) => ({ e, i }))
    .sort((a, b) => orden.get(a.e.categoria)! - orden.get(b.e.categoria)! || a.i - b.i)
    .map((x) => x.e);
})();

const POR_ID = new Map(ENTRADAS.map((e) => [e.id, e]));

export function entradaPorId(id: string): EntradaDeCatalogo | undefined {
  return POR_ID.get(id);
}

/** ¿La entrada afecta a la simulación? (Pisos y muros: no.) */
export function simulaEntrada(e: EntradaDeCatalogo): boolean {
  if (e.tipo === 'via') return e.simula;
  if (e.tipo === 'elemento') return CATALOGO[e.itemId]?.simula ?? false;
  return false;
}

export function descripcionDeEntrada(e: EntradaDeCatalogo): string {
  return e.tipo === 'elemento' ? (CATALOGO[e.itemId]?.descripcion ?? '') : e.descripcion;
}

/** Paso desde el que simula una entrada «Solo visual» (si se sabe). */
export function simulaDesdeEntrada(e: EntradaDeCatalogo): string | undefined {
  if (e.tipo === 'via') return e.simulaDesde;
  if (e.tipo === 'elemento') return CATALOGO[e.itemId]?.simulaDesde;
  return undefined;
}

/**
 * Medidas que muestra la tarjeta: ancho × fondo (m); las vías, su ancho de calzada; el muro, su
 * espesor; la zona libre, ninguna (`null`: se dibuja).
 */
export function medidasDeEntrada(
  e: EntradaDeCatalogo,
): { ancho: number; fondo: number | null } | null {
  if (e.tipo === 'via') return { ancho: ANCHO_VIA[e.tipoVia], fondo: null };
  if (e.tipo === 'muro') return { ancho: MURO_POR_DEFECTO.espesor, fondo: null };
  if (e.tipo === 'piso') return e.medidas ? { ...e.medidas } : null;
  const d = CATALOGO[e.itemId]!.dimensionesPorDefecto;
  return { ancho: d.ancho, fondo: d.fondo };
}

/** Piso nuevo de una entrada rectangular, centrado en `centro`. */
export function nuevoPiso(e: EntradaPiso, id: string, centro: Punto): Piso {
  const m = e.medidas ?? { ancho: 8, fondo: 6 };
  return { id, acabado: e.acabado, puntos: rectangulo(centro, m.ancho, m.fondo) };
}

/** Muro nuevo con los puntos dibujados y las medidas por defecto. */
export function nuevoMuro(id: string, puntos: readonly Punto[]): Muro {
  return { id, puntos: puntos.map((p) => ({ ...p })), ...MURO_POR_DEFECTO };
}

/** Elemento nuevo de una entrada, con las medidas y propiedades por defecto. */
export function nuevoElemento(
  e: EntradaElemento,
  id: string,
  posicion: Punto,
  rotacion = 0,
): Elemento {
  const item = CATALOGO[e.itemId]!;
  const texto = TEXTO_INICIAL[item.id];
  return {
    id,
    catalogoId: item.id,
    posicion: { ...posicion },
    rotacion,
    dimensiones: { ...item.dimensionesPorDefecto },
    ...(e.propiedades ? { propiedades: { ...e.propiedades } } : {}),
    ...(texto ? { etiqueta: texto } : {}),
  };
}

/**
 * Elemento nuevo colocado en `punto`, ya orientado (paso 1.4): su boca mira a la vía más cercana o
 * hereda el giro de sus vecinos; un ingreso o una salida se pega a la vía. Lo usan el fantasma del
 * plano y la colocación, así lo que se ve antes de hacer clic es lo que queda.
 */
export function elementoAlColocar(
  e: EntradaElemento,
  id: string,
  punto: Punto,
  layout: Pick<Layout, 'vias' | 'elementos'>,
  giroExtraGrados = 0,
): Elemento {
  const el = nuevoElemento(e, id, punto);
  const s = colocacionSugerida(el, CATALOGO[e.itemId]!, layout);
  const rotacion = giroExtraGrados
    ? normalizarAngulo(s.rotacion + (giroExtraGrados * Math.PI) / 180)
    : s.rotacion;
  return { ...el, posicion: s.posicion, rotacion };
}

/** Búsqueda por nombre y categoría, sin distinguir mayúsculas ni tildes. */
export function buscarEntradas(texto: string): EntradaDeCatalogo[] {
  const q = normalizar(texto.trim());
  if (!q) return [];
  const nombreCat = new Map(CATEGORIAS.map((c) => [c.id, c.nombre]));
  return ENTRADAS.filter((e) =>
    q
      .split(/\s+/)
      .every((p) => normalizar(`${e.nombre} ${nombreCat.get(e.categoria) ?? ''}`).includes(p)),
  );
}

function normalizar(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}
